import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { REASON_TOOLTIPS, ReasonLabel } from './segments';

interface ReasonTextProps {
  reason: ReasonLabel;
  trailing?: ReactNode;
  className?: string;
  // Optional override for the tooltip body. When provided, replaces the
  // static `REASON_TOOLTIPS[reason.key]` string with arbitrary React content
  // (e.g. the per-phase <2 ADL math derivation). The trigger styling is the
  // same as the default (dotted underline + cursor-help), so users get the
  // same affordance.
  tooltipContent?: ReactNode;
}

/**
 * Renders a reason label with the explanatory tooltip the calc-derived
 * reason key carries. Trailing text (e.g. "90d total", "30d remaining")
 * stays outside the tooltip trigger so it remains plain.
 */
export function ReasonText({ reason, trailing, className, tooltipContent }: ReasonTextProps) {
  const defaultTip = REASON_TOOLTIPS[reason.key];
  const hasTip = tooltipContent != null || defaultTip != null;
  const content = (
    <span className={className ?? `text-sm font-medium ${reason.tone}`}>
      {hasTip ? (
        <span className="cursor-help border-b border-dotted border-gray-400">
          {reason.text}
        </span>
      ) : (
        reason.text
      )}
      {trailing}
    </span>
  );
  if (!hasTip) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        {tooltipContent ?? defaultTip}
      </TooltipContent>
    </Tooltip>
  );
}
